import { IStorageAdapter } from './IStorageAdapter';
import { ApiResponse } from '../../types/api';
import { Attachment, UploadAttachmentDto, AttachmentFilter, AttachmentFileCategory } from '../../types/attachment';

function ok<T>(data: T): ApiResponse<T> {
  return { success: true, data, message: 'OK' };
}

function getFolderPrefix(entityType: string): string {
  const map: Record<string, string> = {
    complaint: 'Complaints',
    work_order: 'WorkOrders',
    vendor: 'Vendors',
    technician: 'Technicians',
  };
  return map[entityType] || 'Misc';
}

function getSubfolder(category: AttachmentFileCategory): string {
  const map: Record<AttachmentFileCategory, string> = {
    photo_before: 'photos/before',
    photo_during: 'photos/during',
    photo_after: 'photos/after',
    document: 'documents',
    quotation: 'documents/quotations',
    report: 'documents/reports',
    invoice: 'documents/invoices',
    other: 'documents/other',
  };
  return map[category];
}

function isImageType(type: string): boolean {
  return type.startsWith('image/');
}

const DEMO_ATTACHMENTS: Attachment[] = [];

export class GASDriveAdapter implements IStorageAdapter {
  private attachments: Attachment[] = [...DEMO_ATTACHMENTS];

  async upload(dto: UploadAttachmentDto): Promise<ApiResponse<Attachment>> {
    await new Promise(r => setTimeout(r, 600));

    const dataUrl = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(dto.file);
    });

    const folderPath = `/NAD_Complaints/${getFolderPrefix(dto.entity_type)}/${dto.entity_id}/${getSubfolder(dto.file_category)}`;

    const attachment: Attachment = {
      id: `ATT-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      entity_type: dto.entity_type,
      entity_id: dto.entity_id,
      file_name: dto.file.name,
      file_type: dto.file.type,
      file_size: dto.file.size,
      file_category: dto.file_category,
      storage_path: `${folderPath}/${dto.file.name}`,
      preview_url: dataUrl,
      thumbnail_url: isImageType(dto.file.type) ? dataUrl : undefined,
      uploaded_by_user_id: 'USR-00001',
      uploaded_by_user_name: 'Current User',
      uploaded_at: new Date().toISOString(),
      notes: dto.notes,
    };

    this.attachments.push(attachment);
    return ok(attachment);
  }

  async delete(attachmentId: string): Promise<ApiResponse<void>> {
    await new Promise(r => setTimeout(r, 300));
    this.attachments = this.attachments.filter(a => a.id !== attachmentId);
    return ok(undefined as unknown as void);
  }

  async getByEntity(filter: AttachmentFilter): Promise<ApiResponse<Attachment[]>> {
    await new Promise(r => setTimeout(r, 300));
    let result = [...this.attachments];
    if (filter.entity_type) result = result.filter(a => a.entity_type === filter.entity_type);
    if (filter.entity_id) result = result.filter(a => a.entity_id === filter.entity_id);
    if (filter.file_category) result = result.filter(a => a.file_category === filter.file_category);
    return ok(result);
  }

  async getPreviewUrl(attachmentId: string): Promise<ApiResponse<string>> {
    await new Promise(r => setTimeout(r, 200));
    const att = this.attachments.find(a => a.id === attachmentId);
    if (!att) return { success: false, data: '', message: 'Attachment not found' };
    return ok(att.preview_url);
  }

  async getThumbnailUrl(attachmentId: string): Promise<ApiResponse<string>> {
    await new Promise(r => setTimeout(r, 200));
    const att = this.attachments.find(a => a.id === attachmentId);
    if (!att) return { success: false, data: '', message: 'Attachment not found' };
    return ok(att.thumbnail_url || att.preview_url);
  }
}
